import React from 'react';

export const PrivacyPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-[var(--surface-1)]">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 animate-fade-in">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl sm:text-5xl font-bold text-[var(--text-1)] font-serif mb-4">
                            Privacy Policy
                        </h1>
                        <p className="text-[var(--text-3)]">Last updated: January 15, 2025</p>
                    </div>

                    <div className="bg-[var(--surface-2)] rounded-3xl shadow-xl border border-[var(--surface-4)] p-8 sm:p-12 space-y-8">
                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">1. Information We Collect</h2>
                            <p className="text-[var(--text-2)] leading-relaxed mb-3">
                                CompareLens is committed to protecting your privacy. We collect and process the following types
                                of information when you use our service:
                            </p>
                            <div className="ml-4 space-y-3">
                                <div>
                                    <h3 className="font-semibold text-[var(--text-1)] mb-2">Information You Provide</h3>
                                    <ul className="list-disc list-inside text-[var(--text-2)] space-y-2 ml-4">
                                        <li>Search queries and laptop comparison requests</li>
                                        <li>Browser and device information</li>
                                        <li>Usage data and preferences stored locally in your browser</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-[var(--text-1)] mb-2">Automatically Collected Information</h3>
                                    <ul className="list-disc list-inside text-[var(--text-2)] space-y-2 ml-4">
                                        <li>IP address and general location data</li>
                                        <li>Browser type and version</li>
                                        <li>Pages visited and time spent on our service</li>
                                        <li>Referring website addresses</li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">2. How We Use Your Information</h2>
                            <p className="text-[var(--text-2)] leading-relaxed mb-3">
                                We use the collected information for the following purposes:
                            </p>
                            <ul className="list-disc list-inside text-[var(--text-2)] space-y-2 ml-4">
                                <li>To provide and improve our AI-powered laptop comparison services</li>
                                <li>To personalize your experience and save your search history locally</li>
                                <li>To analyze usage patterns and optimize our platform performance</li>
                                <li>To detect and prevent technical issues or fraudulent activity</li>
                                <li>To comply with legal obligations and enforce our terms of service</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">3. Data Storage and Security</h2>
                            <p className="text-[var(--text-2)] leading-relaxed mb-3">
                                We take the security of your information seriously and implement appropriate technical and
                                organizational measures:
                            </p>
                            <ul className="list-disc list-inside text-[var(--text-2)] space-y-2 ml-4">
                                <li>Search history is stored locally in your browser using localStorage</li>
                                <li>We use industry-standard encryption for data transmission (HTTPS)</li>
                                <li>Access to any collected data is restricted to authorized personnel only</li>
                                <li>We regularly review and update our security practices</li>
                                <li>We do not sell or rent your personal information to third parties</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">4. Cookies and Tracking Technologies</h2>
                            <p className="text-[var(--text-2)] leading-relaxed mb-3">
                                We use cookies and similar tracking technologies to enhance your experience. For detailed
                                information about the cookies we use and how to manage them, please see our Cookie Policy.
                            </p>
                            <p className="text-[var(--text-2)] leading-relaxed">
                                You can control cookie settings through your browser preferences, though disabling certain
                                cookies may affect the functionality of our service.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">5. Third-Party Services</h2>
                            <p className="text-[var(--text-2)] leading-relaxed mb-3">
                                Our service integrates with third-party services to provide functionality:
                            </p>
                            <ul className="list-disc list-inside text-[var(--text-2)] space-y-2 ml-4">
                                <li>Google Gemini AI for laptop analysis and recommendations</li>
                                <li>Google Search for real-time laptop data and benchmarks</li>
                                <li>External websites for product information and pricing</li>
                            </ul>
                            <p className="text-[var(--text-2)] leading-relaxed mt-3">
                                These third parties have their own privacy policies. We recommend reviewing their policies
                                to understand how they collect and use your information.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">6. Your Rights and Choices</h2>
                            <p className="text-[var(--text-2)] leading-relaxed mb-3">
                                You have the following rights regarding your personal information:
                            </p>
                            <ul className="list-disc list-inside text-[var(--text-2)] space-y-2 ml-4">
                                <li>Access and review the search history stored in your browser</li>
                                <li>Delete your local search history at any time using the Clear History function</li>
                                <li>Opt-out of cookies through your browser settings</li>
                                <li>Request information about data we may have collected</li>
                                <li>Contact us with privacy concerns or questions</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">7. Children's Privacy</h2>
                            <p className="text-[var(--text-2)] leading-relaxed">
                                CompareLens is not intended for children under the age of 13. We do not knowingly collect
                                personal information from children. If you believe we have inadvertently collected information
                                from a child, please contact us immediately so we can take appropriate action.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">8. International Data Transfers</h2>
                            <p className="text-[var(--text-2)] leading-relaxed">
                                Your information may be transferred to and processed in countries other than your own. These
                                countries may have different data protection laws. By using our service, you consent to the
                                transfer of your information to these locations. We take appropriate safeguards to ensure
                                your data remains protected.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">9. Changes to This Policy</h2>
                            <p className="text-[var(--text-2)] leading-relaxed">
                                We may update this Privacy Policy from time to time to reflect changes in our practices or
                                for legal, operational, or regulatory reasons. We will notify you of any material changes by
                                posting the updated policy on this page with a new "Last updated" date. We encourage you to
                                review this policy periodically.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">10. Contact Us</h2>
                            <p className="text-[var(--text-2)] leading-relaxed mb-4">
                                If you have any questions, concerns, or requests regarding this Privacy Policy or our data
                                practices, please contact us:
                            </p>
                            <div className="p-4 bg-[var(--surface-3)] rounded-lg">
                                <p className="text-[var(--text-2)]">
                                    Email: <a href="mailto:davesaumya24@gmail.com" className="text-[var(--brand)] hover:underline">davesaumya24@gmail.com</a>
                                </p>
                            </div>
                        </section>
                    </div>

                    <div className="text-center mt-8">
                        <a
                            href="/"
                            className="text-[var(--brand)] hover:text-[var(--brand-hover)] font-semibold transition-colors"
                        >
                            Back to Home
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};
